import { useEffect, useMemo, useState } from "react";
import type { EChartsOption, SeriesOption } from "echarts";
import EChart from "./charts/EChart";
import { defaultGrid, tooltipCss } from "./charts/chartDefaults";
import { ChartFrame } from "./ChartInspector";
import ChartPlaceholder from "./ChartPlaceholder";
import ChartError from "./ChartError";
import { matrixToDimensionSeries, runPromQueryRange } from "../prometheus/PrometheusClient";
import type { DimensionSeries } from "../config/data";

const TITLE = "Rate-limited bidder requests";

/** Shared by the chart, the loading placeholder and the error state, so the
 * section keeps one footprint across all three. */
const CHART_HEIGHT = 360;

/**
 * Share of incoming bidder requests dropped by the `bid` and `low_performance`
 * rate limiters, split by cluster. Note the regex is anchored by Prometheus, so
 * `low_performance` matches only that limiter — not `low_performance_per_campaign`.
 *
 * All clusters and all instances are included.
 */
const QUERY = [
  'sum(rate(bidder_rate_limited_requests_total{cluster=~".*",instance=~".*",limiter=~"(bid|low_performance)"}[1m])) by (cluster)',
  "/",
  'sum(rate(bidder_request_total{cluster=~".*",instance=~".*"}[1m])) by (cluster)',
].join("\n");

const SPLIT_LABEL = "cluster";

interface RangeOption {
  key: string;
  label: string;
  seconds: number;
  /** Query resolution. Never below 60s — the rate window is [1m]. */
  stepSeconds: number;
}

// Steps are chosen to keep every range at ~350 points, which is roughly one
// sample per horizontal pixel of chart at full width.
const RANGES: RangeOption[] = [
  { key: "7d", label: "7d", seconds: 7 * 24 * 60 * 60, stepSeconds: 30 * 60 },
  { key: "30d", label: "30d", seconds: 30 * 24 * 60 * 60, stepSeconds: 2 * 60 * 60 },
  { key: "90d", label: "90d", seconds: 90 * 24 * 60 * 60, stepSeconds: 6 * 60 * 60 },
];

const DEFAULT_RANGE_KEY = "7d";

/** Ratio → percent, so axis, tooltip and the "View data" table all read as %. */
const TO_PERCENT = 100;

export default function RateLimitedRequestsPlot() {
  const [rangeKey, setRangeKey] = useState(DEFAULT_RANGE_KEY);
  const [data, setData] = useState<DimensionSeries | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Bumped by Retry to re-run the query without changing the range. */
  const [reloadNonce, setReloadNonce] = useState(0);

  const range = RANGES.find(r => r.key === rangeKey) ?? RANGES[0];

  useEffect(() => {
    let cancelled = false;

    setData(null);
    setError(null);

    const end = new Date();
    const start = new Date(end.getTime() - range.seconds * 1000);

    runPromQueryRange(QUERY, start, end, range.stepSeconds)
      .then(series => {
        if (cancelled) return;
        setData(matrixToDimensionSeries(series, SPLIT_LABEL, TO_PERCENT));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [range.seconds, range.stepSeconds, reloadNonce]);

  const option = useMemo<EChartsOption>(() => buildOption(data ?? {}), [data]);

  const rangeToggle = (
    <div className="grain-toggle no-print">
      {RANGES.map(r => (
        <button
          key={r.key}
          type="button"
          className={`grain-toggle__btn${r.key === rangeKey ? " grain-toggle__btn--active" : ""}`}
          onClick={() => setRangeKey(r.key)}
        >
          {r.label}
        </button>
      ))}
    </div>
  );

  if (error) {
    return (
      <div className="plots">
        <h3>{TITLE}</h3>
        <ChartError
          title="Rate-limiting data unavailable"
          detail={error}
          onRetry={() => setReloadNonce(n => n + 1)}
          height={CHART_HEIGHT}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="plots">
        <h3>{TITLE}</h3>
        <ChartPlaceholder height={CHART_HEIGHT} />
      </div>
    );
  }

  return (
    <div className="plots">
      <h3>{TITLE}</h3>
      <ChartFrame
        title={TITLE}
        subtitle="bid + low_performance limiters, share of all bidder requests"
        data={data}
        chartLabel="Dimension line chart"
        axisFormatter={percentAxisLabel}
        headerExtra={rangeToggle}
      >
        <EChart option={option} height={CHART_HEIGHT} />
      </ChartFrame>
    </div>
  );
}

function percentAxisLabel(value: number): string {
  return `${value.toFixed(1)}%`;
}

function buildOption(data: DimensionSeries): EChartsOption {
  const entries = Object.entries(data);

  const series: SeriesOption[] = entries.map(([dimension, timeseries]) => ({
    name: dimension,
    type: "line",
    // Dense operational series (up to ~360 points) — markers would just be noise.
    symbol: "none",
    // Real gaps in the data should read as gaps, not as a drop to zero.
    connectNulls: false,
    data: timeseries.timestamps.map((timestamp, index) => [timestamp.getTime(), timeseries.data[index]]),
  }));

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      extraCssText: tooltipCss,
      valueFormatter: value => (value == null ? "—" : `${Number(value).toFixed(2)}%`),
    },
    legend: { top: 0 },
    grid: defaultGrid,
    xAxis: { type: "time" },
    yAxis: {
      type: "value",
      min: 0,
      axisLabel: { formatter: percentAxisLabel, margin: 12 },
    },
    series,
  } satisfies EChartsOption;
}
